import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    getSiteLocaleFromPathname,
    isValidEmail,
    withSiteLocale,
} from '../../utils/helpers';
import { useAppSelector } from '../../store/hooks';
import { TextField, Title, Button, TextInput, GenericError } from '../common';
import { useTranslation } from '../../hooks/translations/TranslationProvider';
import { contactUs } from '../../api/contactUs';
import useSeo from '../../hooks/useSeo';

import './contactUs.css';

interface ContactFormValues {
    email: string | null;
    title: string | null;
    message: string | null;
}

interface Errors {
    email: string | null;
    title: string | null;
    message: string | null;
}

const ContactUs = () => {
    const location = useLocation();
    const auth = useAppSelector(state => state.auth);
    const { t } = useTranslation();
    const locale = getSiteLocaleFromPathname(location.pathname);
    useSeo({
        title: t('seo.contactUs.title'),
        description: t('seo.contactUs.description'),
        path: '/contact-us',
    });

    const [formValues, setFormValues] = useState<ContactFormValues>({
        email: null,
        title: null,
        message: null,
    });
    const [errors, setErrors] = useState<Errors>({
        email: null,
        title: null,
        message: null,
    });
    const [showThankYou, setShowThankYou] = useState(false);
    const [serverError, setServerError] = useState(null);

    const inputChangeHandler = (name: string, value: string) => {
        setFormValues({
            ...formValues,
            [name]: value,
        });
        setErrors({
            email: null,
            title: null,
            message: null,
        });
    };

    const onSendMessage = async () => {
        if (
            !formValues.email ||
            (formValues.email && !isValidEmail(formValues.email))
        ) {
            setErrors({ ...errors, email: t('errors.invalidEmail') });
            return;
        }

        if (!formValues.title) {
            setErrors({ ...errors, title: t('errors.requiredField') });
            return;
        }

        if (!formValues.message) {
            setErrors({ ...errors, message: t('errors.requiredField') });
            return;
        }

        try {
            const payload = { ...formValues };
            await contactUs(payload);
            setShowThankYou(true);
        } catch (e: any) {
            setServerError(e);
        }
    };

    if (serverError) {
        return <GenericError error={serverError} />;
    }

    return (
        <div className="contact-us">
            {showThankYou ? (
                <div className="contact-us-thank-you">
                    <Title
                        text={t('contactUs.thankYou')}
                        size="xl"
                        color="green"
                    />
                    <TextField>{t('contactUs.thankYouSub')}</TextField>
                    <Link to={withSiteLocale('/', locale)}>
                        <TextField color="green">
                            {t('contactUs.backToHome')}
                        </TextField>
                    </Link>
                </div>
            ) : (
                <div className="contact-us-main">
                    <div className="contact-us-header">
                        <Title text={t('contactUs.title')} size="xxl" />
                        <TextField>{t('contactUs.subtitle')}</TextField>
                    </div>
                    <div className="contact-us-info">
                        <Title text={t('contactUs.infoTitle')} size="xl" />
                        <TextField color="subtitle">
                            {t('contactUs.infoParagraph1')}
                        </TextField>
                        <TextField color="subtitle">
                            {t('contactUs.infoParagraph2')}
                        </TextField>
                        <TextField weight="bold">
                            {t('contactUs.faq1Question')}
                        </TextField>
                        <TextField color="subtitle">
                            {t('contactUs.faq1Answer')}
                        </TextField>
                        <TextField weight="bold">
                            {t('contactUs.faq2Question')}
                        </TextField>
                        <TextField color="subtitle">
                            {t('contactUs.faq2Answer')}
                        </TextField>
                    </div>
                    <div className="contact-us-form">
                        <TextInput
                            name="email"
                            label={t('contactUs.email')}
                            placeholder={t('login.enterEmail')}
                            onChange={inputChangeHandler}
                            value={formValues.email}
                            error={errors.email}
                            className="full-width"
                        />
                        <TextInput
                            name="title"
                            label={t('contactUs.emailTitle')}
                            placeholder={t('contactUs.emailTitle')}
                            onChange={inputChangeHandler}
                            value={formValues.title}
                            error={errors.title}
                            className="full-width"
                        />
                        <TextInput
                            name="message"
                            label={t('contactUs.message')}
                            placeholder={t('contactUs.message')}
                            onChange={inputChangeHandler}
                            value={formValues.message}
                            error={errors.message}
                            className="full-width"
                            isTextArea
                        />
                        <Button
                            text={t('contactUs.sendMessage')}
                            variant="green"
                            size="lg"
                            onClick={onSendMessage}
                            disabled={auth.isLoading}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactUs;
